import React from 'react';
import { CheckCircle, AlertCircle, FileText, Edit } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { WizardAnswers, ProjectDocument, RequiredDocument } from '../../types/wizard';
import wizardSchema from '../../data/wizardSchema.json';

interface ReviewCardProps {
  answers: WizardAnswers;
  documents: ProjectDocument[];
  onEdit?: () => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({
  answers,
  documents,
  onEdit,
  onSubmit,
  isSubmitting = false
}) => {
  const requiredDocuments = wizardSchema.requiredDocuments;
  const allDocumentsUploaded = requiredDocuments.every(reqDoc =>
    documents.some(doc => doc.document_subtype === reqDoc.type.toUpperCase())
  );

  const canSubmit = Object.keys(answers).length === wizardSchema.steps.length && allDocumentsUploaded;

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Récapitulatif de votre projet
          </h2>
          <p className="text-gray-600">
            Vérifiez vos réponses et documents avant de soumettre votre projet
          </p>
        </div>

        <div className="space-y-8">
          {/* Answers Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Vos réponses</h3>
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  <span>Modifier</span>
                </button>
              )}
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {wizardSchema.steps.map((step, index) => {
                const answer = answers[step.key];
                return (
                  <div key={step.key} className="space-y-2">
                    <div className="flex items-start space-x-2">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{step.label}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {answer || <span className="text-red-500">Non renseigné</span>}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Documents Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Documents téléversés</h3>
            
            <div className="space-y-4">
              {requiredDocuments.map((reqDoc) => {
                const uploadedDoc = documents.find(doc => doc.document_subtype === reqDoc.type.toUpperCase());
                
                return (
                  <div key={reqDoc.type} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{reqDoc.label}</p>
                        {uploadedDoc ? (
                          <p className="text-sm text-gray-600">
                            {uploadedDoc.filename} • {(uploadedDoc.file_size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        ) : (
                          <p className="text-sm text-red-600">Document manquant</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      {uploadedDoc ? (
                        <div className="flex items-center space-x-2 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">Téléversé</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 text-red-600">
                          <AlertCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">Requis</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Status Summary */}
          <div className={`p-6 rounded-xl border-2 ${canSubmit ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
            <div className="flex items-center space-x-3">
              {canSubmit ? (
                <>
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <h4 className="font-semibold text-green-900">Dossier complet</h4>
                    <p className="text-green-700">Votre projet est prêt à être soumis pour évaluation.</p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                  <div>
                    <h4 className="font-semibold text-orange-900">Dossier incomplet</h4>
                    <p className="text-orange-700">
                      Veuillez compléter toutes les réponses et téléverser tous les documents requis.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Submit Button */}
          {onSubmit && (
            <div className="text-center">
              <button
                onClick={onSubmit}
                disabled={!canSubmit || isSubmitting}
                className="px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg"
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Soumission en cours...</span>
                  </div>
                ) : (
                  'Soumettre mon projet'
                )}
              </button>
              
              {!canSubmit && (
                <p className="text-sm text-gray-600 mt-2">
                  Complétez votre dossier pour pouvoir le soumettre
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};